import { apiClient } from './client';

export interface SecurityStats {
  activeSessions: number;
  uniqueActiveUsers: number;
  eventsLast24h: Array<{ eventType: string; count: string }>;
  criticalEvents24h: number;
  warningEvents24h: number;
  failedLogins24h: number;
  successfulLogins24h: number;
  revokedSessions24h: number;
  suspiciousActivity7d: number;
  topCountries: Array<{ country: string; count: string }>;
  topPlatforms: Array<{ platform: string; count: string }>;
}

export interface SessionUser {
  id: string;
  email: string | null;
  username: string;
  isAnonymous: boolean;
  isSubscribed: boolean;
}

export interface Session {
  id: string;
  userId: string;
  user: SessionUser | null;
  deviceId: string;
  deviceName: string | null;
  deviceModel: string | null;
  platform: string;
  osVersion: string | null;
  appVersion: string | null;
  ipAddress: string | null;
  country: string | null;
  city: string | null;
  isActive: boolean;
  isCurrent: boolean;
  lastActiveAt: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface AuditLog {
  id: string;
  userId: string | null;
  user: { id: string; email: string | null; username: string } | null;
  sessionId: string | null;
  eventType: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  ipAddress: string | null;
  country: string | null;
  deviceId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SessionsQuery {
  page?: number;
  limit?: number;
  userId?: string;
  isActive?: string;
  platform?: string;
  country?: string;
  search?: string;
}

export interface AuditLogsQuery {
  page?: number;
  limit?: number;
  userId?: string;
  eventType?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export const securityApi = {
  // Статистика
  getStats: async (): Promise<SecurityStats> => {
    const response = await apiClient.get('/admin/security/stats');
    return response.data;
  },

  // Сессии
  getSessions: async (query: SessionsQuery = {}): Promise<PaginatedResponse<Session>> => {
    const params = new URLSearchParams();
    if (query.page) params.append('page', String(query.page));
    if (query.limit) params.append('limit', String(query.limit));
    if (query.userId) params.append('userId', query.userId);
    if (query.isActive) params.append('isActive', query.isActive);
    if (query.platform) params.append('platform', query.platform);
    if (query.country) params.append('country', query.country);
    if (query.search) params.append('search', query.search);
    
    const response = await apiClient.get(`/admin/security/sessions?${params.toString()}`);
    return response.data;
  },

  getSessionDetails: async (id: string) => {
    const response = await apiClient.get(`/admin/security/sessions/${id}`);
    return response.data;
  },

  revokeSession: async (id: string) => {
    const response = await apiClient.delete(`/admin/security/sessions/${id}`);
    return response.data;
  },

  // Audit логи
  getAuditLogs: async (query: AuditLogsQuery = {}): Promise<PaginatedResponse<AuditLog>> => {
    const params = new URLSearchParams();
    if (query.page) params.append('page', String(query.page));
    if (query.limit) params.append('limit', String(query.limit));
    if (query.userId) params.append('userId', query.userId);
    if (query.eventType) params.append('eventType', query.eventType);
    if (query.severity) params.append('severity', query.severity);
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);
    if (query.search) params.append('search', query.search);
    
    const response = await apiClient.get(`/admin/security/audit-logs?${params.toString()}`);
    return response.data;
  },

  // Пользователь
  getUserSecurityHistory: async (userId: string, limit?: number) => {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get(`/admin/security/users/${userId}/history${params}`);
    return response.data;
  },

  revokeAllUserSessions: async (userId: string) => {
    const response = await apiClient.delete(`/admin/security/users/${userId}/sessions`);
    return response.data;
  },
};
